import { ChevronRight, CreditCard } from 'lucide-react';
import { DataTable, type Column } from '@app/layouts/DataTable';
import { Badge } from '@shared/ui/Badge';
import { formatDate } from '@shared/lib/format-date';
import { formatPrice } from '@shared/lib/format-price';
import {
  describeInterval,
  displayName,
  humanizeStatus,
  initialsOf,
  statusTone,
  type PlatformUser,
} from '@features/users/model/user.types';

interface UsersTableProps {
  users: PlatformUser[];
  loading?: boolean;
  error?: string | null;
  /** Opens the assign-plan dialog. Omitted, the actions column is not rendered. */
  onAssignPlan?: (user: PlatformUser) => void;
  /** Opens the user detail dialog. Omitted, rows are not clickable. */
  onOpen?: (user: PlatformUser) => void;
}

/**
 * Column definitions for the users list. The table mechanics — sticky header,
 * sorting, column visibility, empty/loading states — come from `DataTable`.
 */
export function UsersTable({
  users,
  loading = false,
  error = null,
  onAssignPlan,
  onOpen,
}: UsersTableProps) {
  const columns: Column<PlatformUser>[] = [
    {
      key: 'name',
      header: 'Name',
      alwaysVisible: true,
      sortValue: (user) => displayName(user) ?? user.email,
      render: (user) => {
        const name = displayName(user);

        return (
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-500">
              {initialsOf(user)}
            </span>
            <span className="truncate font-medium text-slate-900">
              {name ?? <span className="text-slate-300">—</span>}
            </span>
          </div>
        );
      },
    },
    {
      key: 'email',
      header: 'Email',
      sortValue: (user) => user.email,
      render: (user) => <span className="truncate text-slate-700">{user.email ?? '—'}</span>,
    },
    {
      key: 'plan',
      header: 'Plan',
      sortValue: (user) => user.subscription?.planName,
      render: (user) => {
        const subscription = user.subscription;

        if (!subscription) {
          return <span className="text-slate-300">—</span>;
        }

        const interval = describeInterval(subscription);

        return (
          <>
            <div className="truncate text-slate-900">{subscription.planName ?? 'Unnamed plan'}</div>
            {interval && <div className="truncate text-xs text-slate-400">{interval}</div>}
          </>
        );
      },
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      sortValue: (user) => user.subscription?.price ?? null,
      render: (user) => {
        const price = user.subscription
          ? formatPrice(user.subscription.price, user.subscription.currency)
          : null;

        return price ? (
          <span className="whitespace-nowrap tabular-nums text-slate-900">{price}</span>
        ) : (
          <span className="text-slate-300">—</span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (user) => user.subscription?.status,
      render: (user) =>
        user.subscription ? (
          <Badge tone={statusTone(user.subscription.status)}>
            {humanizeStatus(user.subscription.status)}
          </Badge>
        ) : (
          <span className="text-slate-400">None</span>
        ),
    },
    {
      key: 'renewAt',
      header: 'Renew at',
      align: 'right',
      // Sorted on the raw timestamp, not the formatted text — "1 Sep" would
      // otherwise sort before "6 Aug".
      sortValue: (user) => user.subscription?.currentPeriodEnd,
      render: (user) => (
        <span className="whitespace-nowrap tabular-nums text-slate-600">
          {formatDate(user.subscription?.currentPeriodEnd)}
        </span>
      ),
    },
    {
      key: 'registeredAt',
      header: 'Registered at',
      align: 'right',
      sortValue: (user) => user.createdAt,
      render: (user) => (
        <span className="whitespace-nowrap tabular-nums text-slate-500">
          {formatDate(user.createdAt)}
        </span>
      ),
    },
    {
      key: 'lastSignInAt',
      header: 'Last sign in',
      align: 'right',
      sortValue: (user) => user.lastSignInAt,
      render: (user) => (
        <span className="whitespace-nowrap tabular-nums text-slate-500">
          {user.lastSignInAt ? formatDate(user.lastSignInAt) : <span className="text-slate-300">Never</span>}
        </span>
      ),
    },
    {
      key: 'budgetCount',
      header: 'Budgets',
      align: 'right',
      sortValue: (user) => user.budgetCount,
      render: (user) => (
        <span
          className={user.budgetCount > 0 ? 'tabular-nums text-slate-900' : 'text-slate-300'}
        >
          {user.budgetCount > 0 ? user.budgetCount : '—'}
        </span>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      sortValue: (user) => user.country,
      render: (user) =>
        user.country ? (
          <span className="truncate text-slate-700">{user.country}</span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
  ];

  if (onAssignPlan) {
    columns.push({
      key: 'actions',
      header: '',
      title: 'Actions',
      align: 'right',
      // Not hideable: it is the only way to reach the dialog.
      alwaysVisible: true,
      // Only offered where there is no plan yet. Changing an existing
      // subscription is Stripe's job, not this admin's — the button was a
      // second way to do something this tool should not be the source of.
      render: (user) =>
        user.subscription ? null : (
          <button
            type="button"
            // The row opens the record; this button does something else, so it
            // must not also trigger the row.
            onClick={(event) => {
              event.stopPropagation();
              onAssignPlan(user);
            }}
            title="Assign a plan to this user"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-periscope-300 hover:bg-periscope-50 hover:text-periscope-800"
          >
            <CreditCard aria-hidden className="h-3.5 w-3.5" />
            Assign
          </button>
        ),
    });
  }

  if (onOpen) {
    columns.push({
      key: 'open',
      header: '',
      title: 'Details',
      align: 'right',
      alwaysVisible: true,
      className: 'w-8',
      // The whole row is the target; this is only the affordance saying so.
      render: () => <ChevronRight aria-hidden className="ml-auto h-4 w-4 text-slate-300" />,
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
      <DataTable
        columns={columns}
        rows={users}
        rowKey={(user) => user.id}
        isLoading={loading}
        isError={Boolean(error)}
        errorMessage={error ?? undefined}
        loadingMessage="Loading users…"
        emptyMessage="No users match these filters."
        columnToggle={false}
        dense
        {...(onOpen ? { onRowClick: onOpen } : {})}
      />
    </div>
  );
}
